/**
 * Custom React hook to manage and display loading progress for various asynchronous operations.
 *
 * This hook tracks the progress of loading, fetching, submitting, updating, deleting, or filtering actions,
 * and provides progress percentage, a descriptive step message, and visibility state for a progress indicator.
 * It automatically animates the progress bar and updates the step message based on the current operation.
 *
 * @param {Object} params - The parameters object.
 * @param {boolean} params.isLoading - Indicates if a loading operation is in progress.
 * @param {boolean} params.isFetching - Indicates if a fetching operation is in progress.
 * @param {boolean} [params.isSubmitting=false] - Indicates if a submitting operation is in progress.
 * @param {boolean} [params.isUpdating=false] - Indicates if an updating operation is in progress.
 * @param {boolean} [params.isDeleting=false] - Indicates if a deleting operation is in progress.
 * @param {boolean} [params.isFiltering=false] - Indicates if a filtering operation is in progress.
 *
 * @returns {Object} An object containing:
 *   - {number} progress: The current progress percentage (0-100).
 *   - {string} step: The current step description.
 *   - {boolean} visible: Whether the progress indicator should be visible.
 */
import { useEffect, useState, useRef } from "react";

export default function useLoadingProgress({
  isLoading,
  isFetching,
  isSubmitting = false,
  isUpdating = false,
  isDeleting = false,
  isFiltering = false
}) {
  const [progress, setProgress] = useState(0);
  const [step, setStep] = useState("Loading...");
  const [visible, setVisible] = useState(false);

  const intervalRef = useRef(null);

  useEffect(() => {
    const loading =
      isLoading || isFetching || isSubmitting || isUpdating || isDeleting || isFiltering;

    if (loading) {
      setVisible(true);
      setProgress(1);

      if (isSubmitting) setStep("Adding to DB...");
      else if (isUpdating) setStep("Updating DB...");
      else if (isDeleting) setStep("Deleting from DB...");
      else if (isFiltering) setStep("Getting info from DB...");
      else setStep("Fetching data from DB...");

      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setProgress(prev => {
          if (prev >= 98) return prev;
          return prev + 1;
        });
      }, 40);
    } else {
      clearInterval(intervalRef.current);
      setProgress(100);
      setStep("Completed");

      setTimeout(() => {
        setVisible(false);
        setProgress(0);
      }, 1000);
    }

    return () => clearInterval(intervalRef.current);
  }, [isLoading, isFetching, isSubmitting, isUpdating, isDeleting, isFiltering]);

  return { progress, step, visible };
}